// eItemSearch.js (Refactored with reusable HTML export template, clean requestSerializer handling)

import { responseRejectInterceptor } from "@api/base";
import { eItemApi } from "@api/eitems";
import { NewButton } from "@components/backoffice/buttons/NewButton";
import { ExportReactSearchKitResults } from "@components/backoffice/ExportSearchResults";
import { QueryBuildHelper } from "@components/SearchBar/QueryBuildHelper";
import {
  invenioConfig,
  setReactSearchKitInitialQueryState,
  setReactSearchKitDefaultSortingOnEmptyQueryString,
  setReactSearchKitUrlHandler,
} from "@config";
import history from "@history";
import SearchAggregationsCards from "@modules/SearchControls/SearchAggregationsCards";
import { SearchControls } from "@modules/SearchControls/SearchControls";
import { SearchControlsOverridesMap } from "@modules/SearchControls/SearchControlsOverrides";
import SearchFooter from "@modules/SearchControls/SearchFooter";
import { BackOfficeRoutes } from "@routes/urls";
import React, { Component } from "react";
import { OverridableContext } from "react-overridable";
import { Link } from "react-router-dom";
import {
  EmptyResults,
  Error,
  InvenioSearchApi,
  ReactSearchKit,
  ResultsList,
  ResultsLoader,
  SearchBar,
} from "react-searchkit";
import { Button, Container, Grid, Header } from "semantic-ui-react";
import EItemListEntry from "./EItemListEntry";
import ClearFiltersButton from "@modules/SearchControls/ClearFiltersButton";
import Qs from "qs";
import { generateExportHTML } from "@components/HtmlExportTemplate/htmlExportTemplate";

export class EItemSearch extends Component {
  modelName = "EITEMS";

  searchApi = new InvenioSearchApi({
    axios: {
      url: eItemApi.searchBaseURL,
      withCredentials: true,
    },
    interceptors: {
      response: { reject: responseRejectInterceptor },
    },
  });

  getRequestSerializer = () => this.searchApi?.requestSerializer;

  viewDetails = ({ row }) => (
    <Button
      as={Link}
      to={BackOfficeRoutes.eitemDetailsFor(row.metadata.pid)}
      compact
      icon="info"
      data-test={row.metadata.pid}
    />
  );

  handleExportClick = async (format, size, currentQueryState) => {
    if (format === "pdf") {
      return this.customExportPDF(currentQueryState, size);
    }

    const requestSerializer = this.getRequestSerializer();
    if (!requestSerializer)
      return console.error("Request serializer unavailable.");

    const queryString = requestSerializer.serialize({
      ...currentQueryState,
      size,
    });
    const params = Qs.parse(queryString);
    params.page = 1;
    params.format = format;
    const exportUrl = `${eItemApi.searchBaseURL}?${Qs.stringify(params, {
      arrayFormat: "repeat",
      encode: false,
    })}`;

    window.open(exportUrl, "_blank");
  };

  customExportPDF = async (currentQueryState, size = 1000) => {
    try {
      const requestSerializer = this.getRequestSerializer();
      if (!requestSerializer)
        return console.error("Request serializer unavailable for PDF export.");

      const queryString = requestSerializer.serialize({
        ...currentQueryState,
        size,
      });
      const params = Qs.parse(queryString);
      params.page = 1;
      params.size = size;
      params.sort = "-created";
      params.format = "csv";
      const exportUrl = `${eItemApi.searchBaseURL}?${Qs.stringify(params, {
        arrayFormat: "repeat",
        encode: false,
      })}`;

      const response = await fetch(exportUrl, { credentials: "include" });
      const csvText = await response.text();

      const rows = csvText
        .trim()
        .split("\n")
        .map((line) => line.split(","));
      const headers = rows[0].map((h) => h.replace(/"/g, "").trim());
      const dataRows = rows
        .slice(1)
        .map((row) => row.map((col) => col.replace(/"/g, "").trim()));

      const desiredColumns = [
        "EItem Type",
        "Title",
        "Author(s)",
        "Publication Year",
        "ISBN",
      ];
      const desiredIndexes = desiredColumns
        .map((col) => headers.indexOf(col))
        .filter((idx) => idx !== -1);

      const tableHeaders = desiredColumns.map((h) => `<th>${h}</th>`).join("");
      const tableRows = dataRows
        .map((row) => {
          const cells = desiredIndexes
            .map((idx) => `<td>${row[idx] || ""}</td>`)
            .join("");
          return `<tr>${cells}</tr>`;
        })
        .join("");

      const htmlContent = generateExportHTML({
        reportTitle: "E-Items Export Report",
        generatedDate: new Date().toLocaleString(),
        tableHeaders,
        tableRows,
        showUniversityLogo: true,
        showLrcLogo: true,
        fallbackLogoText: "Library Resource Center",
      });

      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if (printWindow) {
        printWindow.onload = () => setTimeout(() => printWindow.focus(), 500);
      }
    } catch (error) {
      console.error("PDF Export Error:", error);
    }
  };

  render() {
    const helperFields = [
      { name: "title", field: "document.title" },
      {
        name: "author",
        field: "authors.full_name",
        defaultValue: '"Doe, John"',
      },
      { name: "created", field: "_created" },
      { name: "source", field: "source.text" },
    ];

    const initialState = setReactSearchKitInitialQueryState(this.modelName);
    const defaultSorting = setReactSearchKitDefaultSortingOnEmptyQueryString(
      this.modelName
    );
    const urlHandler = setReactSearchKitUrlHandler(this.modelName);

    return (
      <>
        <Header as="h2">E-items</Header>
        <OverridableContext.Provider value={{ ...SearchControlsOverridesMap }}>
          <ReactSearchKit
            searchApi={this.searchApi}
            history={history}
            urlHandlerApi={urlHandler}
            initialQueryState={initialState}
            defaultSortingOnEmptyQueryString={defaultSorting}
          >
            <>
              <Container fluid className="spaced">
                <SearchBar
                  placeholder="Search for eitems..."
                  {...invenioConfig.APP.SEARCH_BAR_PROPS}
                />
                <QueryBuildHelper fields={helperFields} />
              </Container>
              <Grid>
                <Grid.Row columns={2}>
                  <ResultsLoader>
                    <Grid.Column width={3} className="search-aggregations">
                      <Grid.Column width={16}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "1rem",
                          }}
                        >
                          <Header
                            as="h4"
                            content="Filter by"
                            style={{ margin: 0 }}
                          />
                          <ClearFiltersButton module-identifier="eitems" />
                        </div>
                      </Grid.Column>
                      <SearchAggregationsCards modelName={this.modelName} />
                    </Grid.Column>
                    <Grid.Column width={13}>
                      <Grid columns={2}>
                        <Grid.Column width={8}>
                          <NewButton
                            text="Add E-Item"
                            to={BackOfficeRoutes.eitemCreate}
                          />
                        </Grid.Column>
                        <Grid.Column width={8} textAlign="right">
                          <ExportReactSearchKitResults
                            exportBaseUrl={eItemApi.searchBaseURL}
                            onExportClick={this.handleExportClick}
                            supportedFormats={["csv", "pdf"]}
                          />
                        </Grid.Column>
                      </Grid>
                      <Error />
                      <SearchControls
                        modelName={this.modelName}
                        withLayoutSwitcher={false}
                      />
                      <EmptyResults
                        extraContent={
                          <NewButton
                            text="Add E-Item"
                            to={BackOfficeRoutes.eitemCreate}
                          />
                        }
                      />
                      <ResultsList ListEntryElement={EItemListEntry} />
                      <SearchFooter />
                    </Grid.Column>
                  </ResultsLoader>
                </Grid.Row>
              </Grid>
            </>
          </ReactSearchKit>
        </OverridableContext.Provider>
      </>
    );
  }
}
